import { MARD_291 } from './palette';
import type { FocusMode, PatternProject } from '../types';

export const DEFAULT_SMART_BOARD_CONFIG: SmartBoardConfig = {
  serviceUuid: '6e400001-b5a3-f393-e0a9-e50e24dcca9e',
  writeCharacteristicUuid: '6e400002-b5a3-f393-e0a9-e50e24dcca9e'
};

export interface SmartBoardConfig {
  serviceUuid: string;
  writeCharacteristicUuid: string;
}

export interface SmartBoardStatus {
  connected: boolean;
  deviceName: string;
  transport: 'native' | 'web-bluetooth' | 'unavailable';
}

interface BluetoothCharacteristicLike {
  writeValue?: (value: BufferSource) => Promise<void>;
  writeValueWithoutResponse?: (value: BufferSource) => Promise<void>;
}

interface BluetoothServerLike {
  connect: () => Promise<BluetoothServerLike>;
  getPrimaryService: (uuid: string) => Promise<{ getCharacteristic: (uuid: string) => Promise<BluetoothCharacteristicLike> }>;
}

interface BluetoothDeviceLike extends EventTarget {
  name?: string;
  gatt?: BluetoothServerLike;
}

interface NativeBluetoothBridge {
  connect: (config: SmartBoardConfig) => Promise<{ name?: string } | void>;
  disconnect: () => Promise<void> | void;
  send: (message: string) => Promise<void> | void;
}

declare global {
  interface Navigator {
    bluetooth?: { requestDevice: (options: { acceptAllDevices: boolean; optionalServices: string[] }) => Promise<BluetoothDeviceLike> };
  }
  interface Window {
    YXMPDNativeBluetooth?: NativeBluetoothBridge;
  }
}

const encoder = new TextEncoder();

class SmartBoardBridge {
  private characteristic: BluetoothCharacteristicLike | null = null;
  private device: BluetoothDeviceLike | null = null;
  private nativeBridge: NativeBluetoothBridge | null = null;
  private listeners = new Set<(status: SmartBoardStatus) => void>();
  private writeQueue: Promise<void> = Promise.resolve();
  private status: SmartBoardStatus = { connected: false, deviceName: '', transport: 'unavailable' };

  getStatus() { return this.status; }

  getSupport() {
    if (window.YXMPDNativeBluetooth) return { available: true, transport: 'native' as const, reason: '' };
    if (!window.isSecureContext) return { available: false, transport: 'unavailable' as const, reason: '浏览器蓝牙需要 HTTPS 或本机安全环境。' };
    if (navigator.bluetooth) return { available: true, transport: 'web-bluetooth' as const, reason: '' };
    return { available: false, transport: 'unavailable' as const, reason: '当前浏览器未提供 Web Bluetooth；iPad 网页版可先使用触控笔，蓝牙将在原生 APP 中走同一协议。' };
  }

  subscribe(listener: (status: SmartBoardStatus) => void) {
    this.listeners.add(listener);
    listener(this.status);
    return () => { this.listeners.delete(listener); };
  }

  private setStatus(status: SmartBoardStatus) {
    this.status = status;
    this.listeners.forEach((listener) => listener(status));
  }

  async connect(config: SmartBoardConfig) {
    await this.disconnect();
    const support = this.getSupport();
    if (!support.available) throw new Error(support.reason);
    if (window.YXMPDNativeBluetooth) {
      this.nativeBridge = window.YXMPDNativeBluetooth;
      const result = await this.nativeBridge.connect(config);
      this.setStatus({ connected: true, deviceName: result?.name || '智能豆板', transport: 'native' });
    } else {
      const device = await navigator.bluetooth!.requestDevice({ acceptAllDevices: true, optionalServices: [config.serviceUuid] });
      if (!device.gatt) throw new Error('设备没有提供可连接的 GATT 服务');
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService(config.serviceUuid);
      this.characteristic = await service.getCharacteristic(config.writeCharacteristicUuid);
      this.device = device;
      device.addEventListener('gattserverdisconnected', this.handleDisconnected);
      this.setStatus({ connected: true, deviceName: device.name || 'ESP32 智能豆板', transport: 'web-bluetooth' });
    }
    await this.send({ v: 1, type: 'hello', client: 'yxmpd-web', protocol: 'yxmpd-light-v1' });
  }

  private handleDisconnected = () => {
    this.characteristic = null;
    this.device = null;
    this.nativeBridge = null;
    this.setStatus({ connected: false, deviceName: '', transport: this.getSupport().transport });
  };

  async disconnect() {
    if (this.device) this.device.removeEventListener('gattserverdisconnected', this.handleDisconnected);
    if (this.nativeBridge) await this.nativeBridge.disconnect();
    this.characteristic = null;
    this.device = null;
    this.nativeBridge = null;
    this.setStatus({ connected: false, deviceName: '', transport: this.getSupport().transport });
  }

  private send(payload: object) {
    const line = `${JSON.stringify(payload)}\n`;
    this.writeQueue = this.writeQueue.catch(() => undefined).then(async () => {
      if (this.nativeBridge) {
        await this.nativeBridge.send(line);
        return;
      }
      if (!this.characteristic) throw new Error('智能豆板尚未连接');
      const bytes = encoder.encode(line);
      for (let offset = 0; offset < bytes.length; offset += 180) {
        const chunk = bytes.slice(offset, offset + 180);
        if (this.characteristic.writeValueWithoutResponse) await this.characteristic.writeValueWithoutResponse(chunk);
        else if (this.characteristic.writeValue) await this.characteristic.writeValue(chunk);
        else throw new Error('ESP32 写入特征不可用');
      }
    });
    return this.writeQueue;
  }

  async syncProject(project: PatternProject) {
    await this.send({ v: 1, type: 'project.start', id: project.id, width: project.width, height: project.height });
    let cells: Array<[number, number, string, 0 | 1]> = [];
    for (let index = 0; index < project.colorIndices.length; index += 1) {
      if (project.empty[index]) continue;
      cells.push([index % project.width, Math.floor(index / project.width), MARD_291[project.colorIndices[index]]?.code || '', project.completed[index] ? 1 : 0]);
      if (cells.length === 12) {
        await this.send({ v: 1, type: 'project.cells', cells });
        cells = [];
      }
    }
    if (cells.length) await this.send({ v: 1, type: 'project.cells', cells });
    await this.send({ v: 1, type: 'project.end', id: project.id });
  }

  sendFocus(project: PatternProject, selectedColor: number | null, mode: FocusMode) {
    if (!this.status.connected) return Promise.resolve();
    return this.send({ v: 1, type: 'focus', projectId: project.id, mode, color: selectedColor === null ? null : MARD_291[selectedColor]?.code || null });
  }

  sendCell(project: PatternProject, index: number, completed: boolean) {
    if (!this.status.connected) return Promise.resolve();
    return this.send({ v: 1, type: 'cell', projectId: project.id, x: index % project.width, y: Math.floor(index / project.width), color: MARD_291[project.colorIndices[index]]?.code || '', completed: completed ? 1 : 0 });
  }

  sendColorCompletion(project: PatternProject, colorIndex: number, completed: boolean) {
    if (!this.status.connected) return Promise.resolve();
    return this.send({ v: 1, type: 'color.complete', projectId: project.id, color: MARD_291[colorIndex]?.code || '', completed: completed ? 1 : 0 });
  }

  testLights() {
    return this.send({ v: 1, type: 'lights.test', durationMs: 1800 });
  }
}

export const smartBoard = new SmartBoardBridge();
